import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { ElasticVehicle } from '../../@types/ElasticVehicle';
import { clear, find, vehicleSearch as search } from '../../actions/retailer';
import {
  track,
  trackConversionEvent,
  trackExposureEvent,
  trackOtherEvent,
} from '../../actions/tracking';
import { Button } from '../../components/button';
import HeroShell from '../../components/hero/hero-shell';
import MapComponent from '../../components/map-component';
import { Pagination, ResultSet } from '../../components/search';
import { asHtml } from '../../helpers/markdown';
import { createPageTitle } from '../../helpers/text';
import { cdn, cleanup } from '../../helpers/urls';
import useTrackPageView from '../../hooks/useTrackPageView';
import type { RootState } from '../../reducers';
import { getRetailerDocument, isRetailerUsingFreespee } from '../../selectors/retailer';
import { shouldSearchForRetailerVehicles } from '../../selectors/retailers';
import { responseStatus } from '../../shared/statuses';
import { conversionEvents } from '../../shared/tracking';
import Accordion from '../../styled-components/components/Accordion';
import Content from '../../styled-components/components/Content';
import Loader from '../../styled-components/components/Loader';
import { getPhoneParams } from '../../utils';
import { getRetailerSearch, getRetailerSearchPages } from '../../utils/reselectors';
import { resetViewport } from '../../utils/ts/viewport';
import ActionEmail from './action-email';
import ActionPhonenumber from './action-phonenumber';
import BusinessInformation from './business-information';
import MessageToRetailerModal from './message-to-retailer-modal';
import NotFound from './not-found';
import OpeningHours from './opening-hours';
import RetailerPromos from './retailer-promos';

let _imboxInit = false;

const Retailer = () => {
  const urlParams = useParams();
  const slug = urlParams.slug!;
  const { t } = useTranslation(['retailer']);
  const dispatch = useDispatch();

  const notFound = useSelector<RootState, boolean>(
    (s) => s.retailers && s.retailers.responseStatus === responseStatus.FAILED,
  );
  const retailer = useSelector(getRetailerDocument);
  const isFreespeeActive = useSelector(isRetailerUsingFreespee);
  const documents = useSelector(getRetailerSearch);
  const documentPages = useSelector(getRetailerSearchPages);
  const shouldSearch = useSelector(shouldSearchForRetailerVehicles);
  const trackPageViewEvent = useTrackPageView();

  const actions = bindActionCreators(
    { find, search, clear, track, trackConversionEvent, trackOtherEvent, trackExposureEvent },
    dispatch,
  );

  const scrollTargetRef = useRef<HTMLDivElement | null>(null);
  const [messageModal, setMessageModal] = useState(false);

  useEffect(() => {
    resetViewport();
    return () => {
      _imboxInit = false;
    };
  }, []);

  useEffect(() => {
    if (slug && (!retailer || slug !== retailer.slug)) {
      actions.find(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (retailer) {
      document.title = createPageTitle(retailer.name, t('retailers', { ns: 'retailer' }));
      if (window.initImBox && !_imboxInit && retailer && retailer.id) {
        _imboxInit = true;
        window.initImBox(retailer.id);
      }
      trackPageViewEvent();
    }
  }, [retailer]);

  useEffect(() => {
    if (shouldSearch && retailer) {
      actions.search(retailer, 'hits=24');
    }
  }, [shouldSearch]);

  const onToggleMessageModal = () => {
    if (!messageModal) {
      actions.trackOtherEvent(retailer, conversionEvents.MESSAGE_INITIATED_RETAILER);
    }
    setMessageModal((s) => !s);
  };

  const shouldScrollView = () => {
    const scrollTarget = scrollTargetRef.current?.getBoundingClientRect();
    if (scrollTarget !== undefined && scrollTarget.top < 0) {
      document.documentElement.scrollTop = scrollTarget.top + document.documentElement.scrollTop;
    }
  };

  const onApplyFilter = (filter: string) => {
    if (retailer) {
      shouldScrollView();
      actions.search(retailer, filter);
    }
  };

  if (notFound) {
    return <NotFound />;
  }

  if (!retailer) {
    return (
      <div className="m-t">
        <Loader />
      </div>
    );
  }

  const services =
    retailer && retailer.services && retailer.services.length
      ? retailer.services
          .filter((s) => s && s.markdownContent && s.name)
          .map((service) => ({
            label: service.name,
            html: service.markdownContent,
          }))
      : [];

  let map = null;
  let modal = null;

  if (retailer.position && retailer.position.location.lat && retailer.position.location.lon) {
    const marker = [
      {
        latitude: retailer.position.location.lat,
        longitude: retailer.position.location.lon,
        empty: true,
      },
    ];
    map = (
      <MapComponent markers={marker}>
        <OpeningHours retailer={retailer} />
      </MapComponent>
    );
  }

  if (messageModal) {
    modal = <MessageToRetailerModal onClose={onToggleMessageModal} />;
  }

  let retailerServices = null;
  if (retailer.services && retailer.services.length > 0) {
    retailerServices = (
      <div>
        <h2 className="h3">{t('ourServices', { ns: 'retailer' })}</h2>
        {services.map((item) => (
          <Accordion title={item.label} key={item.label}>
            <Content dangerouslySetInnerHTML={{ __html: asHtml(item.html) }} />
          </Accordion>
        ))}
        <div className="m-b-double hidden-lt-md" />
      </div>
    );
  }

  let resultSet = null;
  if (documents.length) {
    resultSet = (
      <div>
        <div ref={scrollTargetRef}>
          <div className="container">
            <h2 className="no-margin">
              {t('carsAtRetailer', { ns: 'retailer', retailer: retailer.name })}
            </h2>
          </div>
        </div>
        <div className="m-b-double" />
        <div className="container m-b-double">
          <ResultSet title={retailer.name} data={documents as unknown as ElasticVehicle[]} />
        </div>
        <div className="container m-b-double">
          <Pagination pages={documentPages} onApplyPage={onApplyFilter} />
        </div>
      </div>
    );
  }

  let websiteAction = null;
  if (retailer.homePage) {
    websiteAction = (
      <li className="m-b">
        <a
          href={retailer.homePage}
          target="_blank"
          onClick={() => actions.trackOtherEvent(retailer, conversionEvents.OUTBOUNDLINK_RETAILER)}
          rel="nofollow noopener noreferrer"
          title={t('visitOurWebsite', { ns: 'retailer' })}
          data-am-button="primary-clear no-padding text-left text-regular square"
        >
          <div className="table">
            <div className="td td-minimal valign-middle">
              <div data-am-icon="primary">
                <i className="icon-share" />
              </div>
            </div>
            <div className="td valign-middle p-l-half">
              <div className="font-regular text-dark-lighten font-size-small m-b-mini">
                {t('visitOurWebsite', { ns: 'retailer' })}
              </div>
              <div>{cleanup(retailer.homePage, ['utm'])}</div>
            </div>
          </div>
        </a>
      </li>
    );
  }

  let waykeMessage = null;
  if (!retailer.hideDirectMessagingOnLandingPage) {
    waykeMessage = (
      <li className="m-b">
        <Button
          modifiers="primary-clear no-padding text-left text-regular square"
          onClick={onToggleMessageModal}
          ariaLabel={t('sendMessage', { ns: 'retailer' })}
        >
          <div className="table">
            <div className="td td-minimal valign-middle">
              <div data-am-icon="primary">
                <i className="icon-message" />
              </div>
            </div>
            <div className="td valign-middle p-l-half">
              <div>{t('sendMessage', { ns: 'retailer' })}</div>
            </div>
          </div>
        </Button>
      </li>
    );
  }

  const logo = retailer.logo
    ? `${retailer.logo}?w=160`
    : '/images/placeholders/placeholder_logo.png';
  const logo2x = retailer.logo
    ? `${retailer.logo}?w=320`
    : '/images/placeholders/placeholder_logo.png';
  const backgroundImage = retailer.featuredImage ? cdn(retailer.featuredImage, '1920x670') : null;

  return (
    <div>
      {modal}
      <div className="m-b-double">
        <HeroShell
          modifiers=""
          imageMobile={backgroundImage || undefined}
          imageDesktop={backgroundImage || undefined}
        >
          <div className="text-center text-light text-shadow">
            <div className="m-b">
              <div data-am-avatar={retailer.useTransparentLogo ? 'transparent' : ''}>
                <div className="avatar-container">
                  <img src={logo} srcSet={`${logo2x} 2x`} alt={retailer.name} className="avatar" />
                </div>
              </div>
            </div>
            <h1 className="no-margin">{retailer.name}</h1>
          </div>
        </HeroShell>
        <div className="m-b-quadruple hidden-lt-md" />
      </div>

      <div className="container m-b-double">
        <div className="row">
          <div className="col-md-7">
            <h2 className="m-b">
              {t('welcomeToRetailer', { ns: 'retailer', retailer: retailer.name })}
            </h2>
            <div
              data-am-content="ignore-headings"
              dangerouslySetInnerHTML={{
                __html: asHtml(retailer.description),
              }}
            />
            <BusinessInformation
              organizationNumber={retailer.organizationNumber || undefined}
              legalName={retailer.legalName || undefined}
            />
            <div className="m-b-double hidden-md" />
          </div>
          <div className="col-md-4 col-md-offset-1">
            <ul data-am-list="">
              {waykeMessage}

              <ActionPhonenumber
                phonenumber={retailer.telephoneNumber}
                isGenerationActive={isFreespeeActive}
                getParams={(c) => getPhoneParams(null, retailer, c)}
                trackerConversion={(e) => actions.trackConversionEvent(retailer, e)}
                trackerOther={(e) => actions.trackOtherEvent(retailer, e)}
              />

              {retailer?.email && (
                <ActionEmail
                  email={retailer.email}
                  trackerConversion={(e) => actions.trackConversionEvent(retailer, e)}
                  trackerOther={(e) => actions.trackOtherEvent(retailer, e)}
                />
              )}
              {websiteAction}
            </ul>
          </div>
        </div>
        <div className="m-b-double hidden-lt-md" />
      </div>

      <div className="container m-b-double">
        <div className="row">
          <div className="col-md-8">
            <RetailerPromos />
          </div>
          <div className="col-md-4 m-b">{retailerServices}</div>
        </div>
      </div>

      <div className="container m-b-double">
        {map}
        <div className="m-b-quadruple hidden-lt-md" />
      </div>

      {resultSet}
    </div>
  );
};

export default Retailer;
